import { SendOrganisationWebhookTestEventDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface SendTestEventResult {
  status: 'success' | 'failed';
}

/**
 * tRPC `webhook.sendTestEvent`, or the v2 `sendOrganisationWebhookTestEventApi`
 * action on the legacy path. Both branches converge on the normalised
 * `{ status }` shape — the tRPC result is already typed, and the Apollo `success`
 * boolean is mapped to `'success' | 'failed'` in `mapGraphqlResponse`. Cache
 * parity: tRPC invalidates the delivery-log + webhook lists; Apollo evicts the
 * matching `organisation_webhook_delivery` / `organisation_webhook` fields.
 */
export const useSendTestEvent = createMutationHook({
  name: 'sendTestEvent',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.webhook.sendTestEvent.mutationOptions({ onSuccess }),
  mapInput: (id: string) => ({ id }),
  mapResponse: (result: {
    status: SendTestEventResult['status'];
  }): SendTestEventResult => ({ status: result.status }),
  invalidate: (trpc) => [
    trpc.frontend.webhook.deliveryLog.list.queryKey(),
    trpc.frontend.webhook.list.queryKey(),
  ],
  graphqlDocument: SendOrganisationWebhookTestEventDocument,
  graphqlVariables: (id) => ({ object: { Id: id } }),
  mapGraphqlResponse: (data): SendTestEventResult => {
    const success = data.sendOrganisationWebhookTestEventApi?.success;
    if (success === undefined) {
      throw new Error('Failed to send test event');
    }

    return { status: success ? 'success' : 'failed' };
  },
  evictFields: ['organisation_webhook_delivery', 'organisation_webhook'],
});
